import { Component, ViewChild, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { State, filterBy, process, CompositeFilterDescriptor, aggregateBy } from '@progress/kendo-data-query';
import * as _ from 'lodash';
import { GridComponent, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { CommonFunctions } from '../static/common-functions';
import { BaseBus } from '../models/BaseBus';
import { GlobalsService } from '../services/globals.service';
import { SelectionEvent } from '@progress/kendo-angular-grid';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { SearchTarget } from '../enums/SearchTarget';
import { DQOIParam } from '../models/DQOIParam';
import { Message } from '../models/message';
import { MessageType } from '../enums/MessageType';
import { DQOIReferenceData } from '../models/DQOIReferenceData';
import { Subscription } from 'rxjs';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { BaseService } from '../services/base.service';
import { RefreshToken } from '../models/RefreshToken';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { AppClaims } from '../models/AppClaims';
import { CommonParam } from '../models/CommonParam';
import { EmptyOverridable, validateOverridables } from '../decorators/overridable/base-method-override-decorator';
import { CommonValues } from '../static/CommonValues';

@Component({
    selector: '',
    template: ''
})
export abstract class BaseGridUIComponent<T extends BaseBus, D, S extends BaseService> implements OnInit, OnDestroy {
    constructor(public globals: GlobalsService) {
        this.allData = this.allData.bind(this);
        this.pageSize = this.globals.grid_PageSize;
        validateOverridables(this, Object.getPrototypeOf(this).constructor.name, BaseGridUIComponent);
    }

    @EmptyOverridable()
    clearFilters() { }

    @EmptyOverridable()
    protected getData(){}

    private _rights: AppClaims | null = null;
    virtualizationSettings = CommonValues.virtualizationSettings;

    /**
     * Using this getter can cause race conditions in code depending on how this is used.
     * While we have a user data  resolver in place to help, this still could cause issues.
     * If you need reliable data from the user object, use CmProAuthService.subscribeToLoginEvents and CmProAuthService.unsubscribeToLoginEvents in your component
     *
     * @readonly
     * @type {AppClaims}
     * @memberof BaseGridUIComponent
     */
    get rights(): AppClaims {
        return this._rights ?? this.globals.user?.appClaims ?? new AppClaims();
    }

    set rights(value: AppClaims) {
        this._rights = value;
    }
    public virtual: any = {
        itemHeight: 28,
    };
    @Output() doAction: EventEmitter<CommonParam> = new EventEmitter<CommonParam>();
    @Input() refreshFlag: boolean;
    searchTargetEnum: any = SearchTarget;
    goTo(data: any, target: SearchTarget) {
        let param: DQOIParam = new DQOIParam();
        param.target = target;
        param.id = data;
        param.code = "GOTO";
        this.sendParentMessage(MessageType.goTo, null, null, param);

    }
    refreshSubs: Subscription;
    ngOnInit() {
        if (this.refreshFlag == true && this.svc_data) {
            this.refreshSubs = this.svc_data.refreshSubject.subscribe((token: RefreshToken) => {
                this.refreshData(token);
            });
        }
    }
    ngOnDestroy() {
        CommonFunctions.unsubscribe(this.refreshSubs);
    }
    refreshData(token: RefreshToken) {
        if (token.all) {
            this.getData();
        }
    }
    sendParentMessage(type: MessageType, subject: string = null, message: string = null, values: any = null, value: any = null) {
        let msg: Message = new Message();
        msg.messageType = type;
        msg.messageSubject = subject;
        msg.message = message;
        msg.values = values;
        msg.value = value;
        this.parentMessage.emit(msg);
    }
    showDQOI(id: any) {
        let msg: Message = new Message();
        msg.messageType = MessageType.dqoiPopup;
        msg.value = id;
        this.sendParentMessage(MessageType.dqoiPopup, null, null, null, id);
    }

    @Output() parentMessage: EventEmitter<Message> = new EventEmitter<Message>();
    @Input() gridData: T[];
    originalGridData: T[];
    @Input() set ListData(lData: T[]) {
        if (CommonFunctions.hasArrayElementValue(lData)) {
            this.gridData = lData;
            this.cloneData();
        }
        else {
            this.clear();
        }
        this.processGridData();

    }

    @Input() set id(value: any) {
        if (CommonFunctions.hasValue(value)) {
            this._id = value;
            this.getData();
        }
        else {
            this.clear();
        }
        this.processGridData();
    }
    _id: any;
    subs_data: Subscription;
    svc_data: S;
    selectedData: T;

    @ViewChild('grid') protected grid: GridComponent;
    @ViewChild('detail') protected detail: D;
    @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

    @Output() selected: EventEmitter<any> = new EventEmitter();
    @Input() readOnly: boolean = false;
    @Input() referenceData: DQOIReferenceData;
    @Input() dataLoading: boolean = false;
    pageSize: number;
    skip: number;

    rowSelected(e: SelectionEvent) {
        this.selectedData = e.selectedRows[0].dataItem;
        this.select(e.selectedRows[0].dataItem);
    }
    select(e: T) {
        this.selected.emit(e);
    }

    public filterSettings: DropDownFilterSettings = {
        caseSensitive: false,
        operator: 'contains'
    };
    public filter: CompositeFilterDescriptor;
    public filterChange(filter: CompositeFilterDescriptor): void {
        this.filter = filter;
        this.gridData = filterBy(this.originalGridData, filter);
        this.processGridData();
    }
    public aggregates: any[] = [];
    public aggregateResults;

    public state: State = {
        skip: 0,
        take: 1000,
        group: []
    };
    public dataStateChange(state: DataStateChangeEvent): void {
        if (state && state.group) {
            state.group.map(group => group.aggregates = this.aggregates);
        }
        this.state = state;
    }
    processGridData() {
        if (CommonFunctions.hasArrayElementValue(this.gridData)) {
            this.aggregateResults = aggregateBy(this.gridData, this.aggregates);
        }
    }

    get gridData_Length(): number {
        return CommonFunctions.hasArrayElementValue(this.gridData) ? this.gridData.length : 0;
    }
    get gridLength(): number {
        //TODO BW: set up height for responsive screens.
        return this.gridData_Length > this.globals.grid_LineCount ? this.globals.grid_Height : null;
    }
    cloneData() {
        this.originalGridData = _.cloneDeep(this.gridData);
    }
    clear(clearId: boolean = true, clearFiltersFlag: boolean = false) {
        if (clearId) {
            this._id = null;
        }
        this.gridData = [];
        this.cloneData();
        this.processGridData();
        if (clearFiltersFlag === true) {
            this.clearFilters();
        }
    }

    public iconClass: string = 'fa fa-2x fa-download gridExportBtnClass';
    public exportData: Array<any> = [
        {
            text: 'Excel',
            icon: 'excel'
        }, {
            text: 'Pdf',
            icon: 'pdf'
        }];

    public onSplitButtonClick(e: string): void {

        if (e === "Pdf") {
            this.exportGridToPDF();
        }
        else {
            this.exportGridToExcel();
        }
    }

    public exportGridToPDF(): void {
        this.grid.saveAsPDF();
    }
    public exportGridToExcel(): void {
        this.grid.saveAsExcel();
    }
    public showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if ((element.nodeName === 'TD' || element.nodeName === 'TH')
            && element.offsetWidth < element.scrollWidth) {
            this.tooltipDir.toggle(element);
        } else {
            this.tooltipDir.hide();
        }
    }
    public allData(indata: ExcelExportData): ExcelExportData {
        if (!this.gridData) this.gridData = [];
        const result: ExcelExportData = {
            data: process(this.gridData as any, { filter: this.filter }).data,
        };
        return result;
    }
    doParentAction(id: any, actionCode: string, dataCode: string) {
        let param: CommonParam = new CommonParam();
        param.addParam("ACTION", actionCode);
        param.addParam("CODE", dataCode);
        param.addParam("ID", id);
        this.doAction.emit(param);
    }
    doParentActionParam(param: CommonParam) {
        this.doAction.emit(param);
    }
}

